import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { Typography, Box } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { useParams } from 'react-router';
import EditableTable, { EditableTableColumn, EditableTableRef } from './common/EditableTable';
import { EditableTableConfig } from './common/types';
import { NEW_ROW_ID } from './common/useInlineEditing';
import { apiService } from '../../services/api';
import { formatNumber } from '../../utils/formatters';
import { Ertragsprognose } from '../../types';
import YieldForecastDeductionsContent from '../pages/projectYield/sections/YieldForecastDeductionsContent';
import ToggleChip from '../common/inputs/ToggleChip';
import { useAuth } from '../../auth/AuthProvider';
import { colors } from '../../styles/colors';

/**
 * Interface für die Tabellendaten (erweitert Ertragsprognose um Felder für die Anzeige)
 */
interface YieldForecastTableData extends Ertragsprognose {
    abzuegeGesamt?: number;
}

interface YieldForecastTableProps {
    gutachten: YieldForecastTableData[];
    canEdit?: boolean;
    /** Callback: Prognose wurde erstellt – Backend-Response direkt einsetzen */
    onForecastCreated?: (forecast: Ertragsprognose) => void;
    /** Callback: Prognose wurde aktualisiert – Backend-Response direkt einsetzen */
    onForecastUpdated?: (forecast: Ertragsprognose) => void;
    /** Callback: Prognose wurde gelöscht – lokal aus Array entfernen */
    onForecastDeleted?: (forecastId: string) => void;
    /** Callback: DeductionItem geändert – einzelne Prognose nachladen */
    onDeductionChanged?: (forecastId: string) => void;
}

/**
 * Yield Forecast Table Component
 * Zeigt alle Ertragsprognosen in einer editierbaren Tabelle an
 * Verwendet EditableTable für Inline-Editing (wie LoanTable)
 * 
 * Features:
 * - Inline-Editing für Name, Bruttoertrag, Unsicherheit
 * - Toggle-Chip für Aktiv-Status
 * - Expandierbare Zeilen für Abzüge-Details
 */
const YieldForecastTable = forwardRef<EditableTableRef, YieldForecastTableProps>(({
    gutachten,
    canEdit = false,
    onForecastCreated,
    onForecastUpdated,
    onForecastDeleted,
    onDeductionChanged,
}, ref) => {
    const { projectId } = useParams<{ projectId: string }>();
    const { user } = useAuth();
    const [isTogglingActive, setIsTogglingActive] = useState(false);

    // State für expandierte Zeile
    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

    /**
     * Handler für Aktiv-Status-Toggle
     * Nutzt die Backend-Response direkt für lokales Update (kein Reload)
     */
    const handleToggleActive = useCallback(async (forecastId: string, currentStatus: boolean, e: React.MouseEvent) => {
        e.stopPropagation(); // Verhindert Row-Click (Edit-Modus)

        if (!projectId || isTogglingActive) return;

        setIsTogglingActive(true);
        try {
            const newStatus = !currentStatus;
            const updated = await apiService.updateYieldForecast(projectId, forecastId, { aktiv: newStatus });
            onForecastUpdated?.(updated as Ertragsprognose);
        } catch (error) {
        } finally {
            setIsTogglingActive(false);
        }
    }, [projectId, isTogglingActive, onForecastUpdated]);

    const handleToggleExpand = useCallback((forecastId: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Verhindert Edit-Modus
        setExpandedRowId(prev => prev === forecastId ? null : forecastId);
    }, []);

    const handleCloseExpanded = useCallback(() => {
        setExpandedRowId(null);
    }, []);

    const safeGutachten = Array.isArray(gutachten) ? gutachten : [];

    // Spalten-Definition mit Edit-Konfiguration
    const columns: EditableTableColumn[] = useMemo(() => [
        {
            key: 'aktiv',
            label: 'Aktiv',
            align: 'left',
            width: '10%',
            sortable: true,
            // Keine editConfig - wird über Toggle-Chip gesteuert
        },
        {
            key: 'name',
            label: 'Name',
            align: 'left',
            width: '25%',
            sortable: true,
            editConfig: {
                type: 'text',
                required: true,
            },
        },
        {
            key: 'bruttoertrag',
            label: 'Bruttoertrag',
            align: 'right',
            width: '20%',
            sortable: true,
            editConfig: {
                type: 'number',
                suffix: 'MWh',
                required: true,
                allowDecimals: false,
                min: 0,
            },
        },
        {
            key: 'unsicherheit',
            label: 'Unsicherheit',
            align: 'right',
            width: '15%',
            sortable: true,
            editConfig: {
                type: 'number',
                suffix: '%',
                allowDecimals: true,
                min: 0,
                max: 100,
            },
        },
        {
            key: 'abzuege_gesamt',
            label: 'Abzüge',
            align: 'right',
            width: '15%',
            sortable: true,
            // Keine editConfig - wird über expandierte Zeile bearbeitet
        },
    ], []);

    // Edit-Konfiguration für die Tabelle
    const editConfig: EditableTableConfig<YieldForecastTableData> = useMemo(() => ({
        fields: {
            name: { type: 'text', required: true },
            bruttoertrag: { type: 'number', suffix: 'MWh', required: true, allowDecimals: false, min: 0 },
            unsicherheit: { type: 'number', suffix: '%', allowDecimals: true, min: 0, max: 100 },
        },
        onCreate: async (data: Partial<YieldForecastTableData>) => {
            if (!projectId) throw new Error('Keine Projekt-ID');

            // Benutzername für erstellt_von
            const userName = user
                ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username
                : 'Unbekannt';

            // Erstelle neue Ertragsprognose mit Default-Werten
            // Abzüge werden über DeductionItems im Backend gesteuert (DEFAULT_DEDUCTIONS)
            const forecastData: Partial<Ertragsprognose> = {
                name: data.name || 'Neue Ertragsprognose',
                bruttoertrag: data.bruttoertrag,
                unsicherheit: data.unsicherheit,
                aktiv: true, // Neue Prognosen sind standardmäßig aktiv
                // Pflichtfelder für Backend
                erstellt_von: userName,
                erstellt_am: new Date().toISOString(),
            };

            const created = await apiService.createProjectErtragsprognose(projectId, forecastData);
            // Backend-Response direkt lokal einsetzen statt Reload
            onForecastCreated?.(created as Ertragsprognose);
            return created as YieldForecastTableData;
        },
        onUpdate: async (forecastId: string, changes: Partial<YieldForecastTableData>) => {
            if (!projectId) throw new Error('Keine Projekt-ID');

            // Konvertiere zu Update-Daten
            const updateData: Partial<Ertragsprognose> = {};
            if (changes.name !== undefined) updateData.name = changes.name;
            if (changes.bruttoertrag !== undefined) updateData.bruttoertrag = changes.bruttoertrag;
            if (changes.unsicherheit !== undefined) updateData.unsicherheit = changes.unsicherheit;

            const updated = await apiService.updateYieldForecast(projectId, forecastId, updateData);
            // Backend-Response direkt lokal einsetzen statt Reload
            onForecastUpdated?.(updated as Ertragsprognose);
            return updated as YieldForecastTableData;
        },
        onDelete: async (forecastId: string) => {
            if (!projectId) throw new Error('Keine Projekt-ID');
            await apiService.deleteYieldForecast(projectId, forecastId);
            // Schließe expandierte Zeile wenn gelöscht
            if (expandedRowId === forecastId) {
                setExpandedRowId(null);
            }
            // Lokal aus Array entfernen statt Reload
            onForecastDeleted?.(forecastId);
        },
        validateRow: (data: Partial<YieldForecastTableData>) => {
            const errors: Record<string, string> = {};

            // Name ist Pflichtfeld
            if (!data.name || data.name.trim() === '') {
                errors.name = 'Name ist erforderlich';
            }

            // Bruttoertrag muss positiv sein
            if (data.bruttoertrag !== undefined && data.bruttoertrag < 0) {
                errors.bruttoertrag = 'Bruttoertrag muss positiv sein';
            }

            // Unsicherheit muss zwischen 0 und 100 liegen
            if (data.unsicherheit !== undefined && (data.unsicherheit < 0 || data.unsicherheit > 100)) {
                errors.unsicherheit = 'Unsicherheit muss zwischen 0% und 100% liegen';
            }

            return errors;
        },
    }), [projectId, onForecastCreated, onForecastUpdated, onForecastDeleted, expandedRowId, user]);

    // Custom Cell-Renderer für spezielle Formatierung im Anzeigemodus
    const renderCell = useCallback((column: EditableTableColumn, row: YieldForecastTableData, value: any) => {
        switch (column.key) {
            case 'aktiv':
                return (
                    <ToggleChip
                        value={value as boolean}
                        onToggle={(e) => handleToggleActive(row.id, value as boolean, e)}
                        disabled={isTogglingActive}
                    />
                );

            case 'name':
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: "medium"
                    }}>
                        {value || '-'}
                    </Typography>
                );

            case 'bruttoertrag':
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: "medium"
                    }}>
                        {formatNumber(value, 0)}MWh
                                            </Typography>
                );

            case 'unsicherheit':
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: "medium"
                    }}>
                        {value != null ? `${formatNumber(value, 1)} %` : '-'}
                    </Typography>
                );

            case 'abzuege_gesamt':
                // Neue Zeilen haben keine Abzüge - nicht expandierbar
                const isNewRow = row.id === NEW_ROW_ID;
                if (isNewRow) {
                    return (
                        <Typography variant="body2" sx={{
                            color: "text.secondary"
                        }}>-
                                                    </Typography>
                    );
                }

                // Klickbar zum Expandieren/Kollabieren
                const isExpanded = expandedRowId === row.id;
                return (
                    <Box
                        onClick={(e) => handleToggleExpand(row.id, e)}
                        sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                            padding: '4px 8px',
                            borderRadius: 1,
                            backgroundColor: isExpanded ? 'action.selected' : 'transparent',
                            '&:hover': {
                                backgroundColor: isExpanded ? 'action.selected' : 'action.hover',
                            },
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: "medium",
                                color: isExpanded ? 'primary.main' : 'inherit'
                            }}>
                            {value != null ? `${formatNumber(value, 2)} %` : '-'}
                        </Typography>
                        {isExpanded ? (
                            <ExpandLessIcon fontSize="small" sx={{ color: colors.tableActions.expand.active }} />
                        ) : (
                            <ExpandMoreIcon fontSize="small" sx={{ color: colors.tableActions.expand.inactive }} />
                        )}
                    </Box>
                );

            default:
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: "medium"
                    }}>
                        {value}
                    </Typography>
                );
        }
    }, [handleToggleActive, isTogglingActive, expandedRowId, handleToggleExpand]);

    /**
     * Rendert den Inhalt der expandierten Zeile.
     * Bei DeductionItem-Änderungen wird nur die betroffene Prognose nachgeladen.
     */
    const renderExpandedRow = useCallback((row: YieldForecastTableData) => {
        return (
            <YieldForecastDeductionsContent
                forecast={row}
                canEdit={canEdit}
                onDataChanged={() => onDeductionChanged?.(row.id)}
            />
        );
    }, [canEdit, onDeductionChanged]);

    try {
        return (
            <EditableTable<YieldForecastTableData>
                ref={ref}
                columns={columns}
                data={safeGutachten}
                editConfig={editConfig}
                canEdit={canEdit}
                renderCell={renderCell}
                actionsColumnWidth="10%"
                actionsColumnAlign="right"
                variant="default"
                showBorder={false}
                minWidth={800}
                emptyMessage="Es wurden noch keine Ertragsprognosen erfasst"
                defaultSortColumn="name"
                defaultSortDirection="asc"
                // Expandierbare Zeilen für Abzüge
                renderExpandedRow={renderExpandedRow}
                expandedRowId={expandedRowId}
                onExpandRow={setExpandedRowId}
            />
        );
    } catch (error) {
        return (
            <Box sx={{
                p: 2
            }}>
                <Typography color="error" variant="body1">
                    Fehler beim Laden der Ertragsprognosen-Tabelle: {error instanceof Error ? error.message : 'Unbekannter Fehler'}
                </Typography>
            </Box>
        );
    }
});

YieldForecastTable.displayName = 'YieldForecastTable';

export default YieldForecastTable;
